import {
  CropperAspect,
  ImageStepInstructions,
  ImageWithCrop,
  useIsMobile,
  ImageDropzone,
  ImageValue,
  generateRandomId,
  FILE_MAX_SIZE,
  BaseUser
} from '@legit.health/ui';
import CropIcon from '@mui/icons-material/Crop';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {Box, Container, ListItem, ListItemText, ListItemIcon, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import TemporaryImages from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/TemporaryImages';
import useImageMaxFileSize from '@/shared/hooks/useImageMaxFileSize';

export default function UploadImage({
  onChange,
  patient,
  t,
  value,
  withTemporaryImages
}: {
  onChange: (image: ImageValue) => void;
  patient: BaseUser;
  t: TFunction;
  value: ImageValue;
  withTemporaryImages: boolean;
}) {
  const isMobile = useIsMobile();
  const customMaxFileSize = useImageMaxFileSize();

  function handleChange(newValue: ImageWithCrop | null) {
    if (newValue) {
      onChange({
        id: value.id,
        ...newValue
      });
    } else {
      onChange({
        id: value.id,
        file: null,
        crop: null
      });
    }
  }

  function handleTemporaryImagesChange(blob: Blob[]) {
    if (blob.length === 0) {
      return;
    }
    onChange({
      id: generateRandomId(),
      file: blob[0] as Blob,
      crop: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      }
    });
  }

  return (
    <div>
      <Container maxWidth="md">
        {!isMobile && withTemporaryImages && (
          <Box
            sx={{
              mb: 4
            }}
          >
            <TemporaryImages
              t={t}
              patient={patient}
              onChange={handleTemporaryImagesChange}
              maxImages={1}
            />
          </Box>
        )}
        <ImageStepInstructions
          dontDrawPrimary={t('ui:diagnosticReport.selectImage.dontDraw.primary')}
          dontDrawSecondary={t('ui:diagnosticReport.selectImage.dontDraw.secondary')}
          combinePrimary={t('ui:diagnosticReport.selectImage.dontCombine.primary')}
          combineSecondary={t('ui:diagnosticReport.selectImage.dontCombine.secondary')}
        />
        <Box
          sx={{
            mb: {
              xs: 3,
              lg: 5
            },
            mt: 2
          }}
        >
          {value.crop && (
            <ListItem>
              <ListItemIcon color="primary">
                {isMobile ? <TouchAppIcon color="primary" /> : <CropIcon color="primary" />}
              </ListItemIcon>
              <ListItemText
                primary={t('ui:diagnosticReport.uploader.cropImage')}
                secondary={
                  isMobile
                    ? t('ui:diagnosticReport.uploader.mobileInstructions')
                    : t('ui:diagnosticReport.uploader.desktopInstructions')
                }
              />
            </ListItem>
          )}
          <ImageDropzone
            onChange={handleChange}
            withCropper
            cropperAspect={CropperAspect.FREE}
            {...(customMaxFileSize && {maxFileSize: customMaxFileSize})}
            helpText={
              <Typography
                sx={{
                  textAlign: 'center'
                }}
              >
                {t('ui:diagnosticReport.uploader.instructions')}
              </Typography>
            }
            value={value}
            dataTestId="dropzoneContainer"
            sizeErrorMessage={t('ui:dropzone.error.fileSize', {
              maxSize: `${(customMaxFileSize || FILE_MAX_SIZE) / 1000000} MB`
            })}
          />
        </Box>
      </Container>
    </div>
  );
}
